import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearchComponent from './SearchMC';
import IconMC from './IconMC';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * AsyncPaginatedSelectMC component
 * A dropdown component with async pagination support, styled exactly like DropdownMC
 *
 * @param {string} label - Text label to display above the dropdown.
 * @param {Function} loadOptions - Async function to load options (receives inputValue, loadedOptions, { page })
 * @param {Function} onSelect - Function to be called when an option is selected.
 * @param {string | number} [defaultValue] - The initial value to be pre-selected.
 * @param {string} [placeholder] - Placeholder text to display when no option is selected.
 * @param {boolean} [search] - If true, a search input will be displayed (default: true).
 * @param {string} [className] - Additional classes to apply to the dropdown.
 */
const AsyncPaginatedSelectMC = ({
  label,
  loadOptions,
  onSelect,
  defaultValue,
  placeholder,
  search = true,
  className = '',
  useFloatingLabel = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const listRef = useRef(null);

  const domNode = useClickOutside(() => {
    setIsOpen(false);
  });

  // Load initial options when dropdown opens
  const loadInitialOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await loadOptions(searchTerm, [], { page: 1 });
      setOptions(result.options || []);
      setHasMore(result.hasMore || false);
      setPage(2);
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadOptions, searchTerm]);

  // Load more options when scrolling
  const loadMoreOptions = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const result = await loadOptions(searchTerm, options, { page });
      setOptions((prev) => [...prev, ...(result.options || [])]);
      setHasMore(result.hasMore || false);
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error('Error loading more options:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadOptions, searchTerm, options, page, isLoading, hasMore]);

  // Reset and reload when search term changes
  useEffect(() => {
    if (isOpen) {
      setOptions([]);
      setPage(1);
      setHasMore(true);
      loadInitialOptions();
    }
  }, [searchTerm, isOpen]);

  // Load initial value label
  useEffect(() => {
    if (defaultValue && !selectedLabel) {
      loadOptions('', [], { page: 1 }).then((result) => {
        const found = (result.options || []).find(
          (opt) => String(opt.value) === String(defaultValue)
        );
        if (found) {
          setSelectedLabel(found.label);
          setSelectedValue(found.value);
        }
      });
    }
  }, [defaultValue]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreOptions();
    }
  }, [loadMoreOptions]);

  const handleSelect = (option) => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option.value);
    }
  };

  const displayedValue = selectedLabel || placeholder || t('customer.view_list.modal_filters.all');

  const buttonClassName = [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-px-4',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    useFloatingLabel ? 'tw-pt-5 tw-pb-1' : 'tw-py-3'
  ].join(' ');

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-text-xs',
    'tw-text-blue-500',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-start-4'
  ].join(' ');

  const dropdownClass = [
    'tw-absolute',
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-50',
    'tw-w-full',
    'tw-mt-1'
  ].join(' ');

  const listItemClassName = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' ');

  return (
    <div className={`tw-wrapper tw-relative tw-w-full ${className}`} ref={domNode}>
      {!useFloatingLabel && label && (
        <label className="tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName}
      >
        <div className="tw-w-full">
          {useFloatingLabel && label && (
            <label className={floatingLabelClassNames}>
              {label}
            </label>
          )}
          <span className="tw-flex tw-items-center tw-overflow-hidden tw-w-full">
            <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
              {displayedValue}
            </span>
          </span>
          <div className="tw-absolute tw-top-[34%] tw-right-4">
            <IconMC
              name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
              size="base"
              className="tw-text-gray-600"
            />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className={dropdownClass}>
          {search && (
            <div className="tw-px-2 tw-pt-2">
              <SearchComponent
                placeholder="Buscar..."
                onChange={setSearchTerm}
                inputClassName="tw-text-sm tw-bg-white"
                debounceTime={300}
              />
            </div>
          )}
          <ul
            ref={listRef}
            onScroll={handleScroll}
            className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0"
          >
            {options.length === 0 && !isLoading ? (
              <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                {t('dropdown_mc.no_options')}
              </li>
            ) : (
              options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`${listItemClassName} ${
                    String(selectedValue) === String(option.value)
                      ? 'tw-bg-blue-500 tw-text-white'
                      : ''
                  }`}
                >
                  <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-w-4/5">
                    {option.label}
                  </span>
                </li>
              ))
            )}
            {isLoading && (
              <li className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-center">
                {t('customer.view_list.modal_filters.loading')}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AsyncPaginatedSelectMC;
